/**
 * Simplified Extension Service
 * Updated for direct messaging approach (v3.0)
 * 
 * This service handles receiving jobs directly from the JobJourney Chrome extension
 * without complex bidirectional communication or API processing.
 */

import { useRef } from 'react';

import {
  getJobStatistics,
  filterExtensionJobs,
  initializeJobProcessing,
  validateExtensionJobData,
  convertExtensionJobsToJobMarketDto
} from './scraping';
import {
  onJobsError,
  onJobsReceived,
  onChunkProgress,
  initializeMessaging,
  clearExtensionJobsFlag,
  hasReceivedExtensionJobs,
  cleanup as cleanupMessaging
} from './messaging';

import type {
  JobsErrorHandler,
  JobsReceivedHandler,
  ChunkProgressHandler,
  ExtensionServiceConfig
} from './types';

// Export types for external use
export * from './types';

// Export utility functions
export { 
  getJobStatistics,
  filterExtensionJobs,
  validateExtensionJobData,
  convertExtensionJobsToJobMarketDto
};

/**
 * Simplified Extension Service Class
 */
class ExtensionService {
  private static instance: ExtensionService;

  private initialized = false;

  private config: ExtensionServiceConfig = { debug: false };

  private constructor() {
    // Private constructor for singleton pattern
  }

  /**
   * Get singleton instance
   */
  public static getInstance(): ExtensionService {
    if (!ExtensionService.instance) {
      ExtensionService.instance = new ExtensionService();
    }
    return ExtensionService.instance;
  }

  /**
   * Initialize the extension service
   */
  public async initialize(options: ExtensionServiceConfig = {}): Promise<void> {
    if (this.initialized) {
      return;
    }

    this.config = { ...this.config, ...options };
    
    if (this.config.debug) {
      console.log('[Extension Service] Initializing simplified extension service v3.0');
    }

    // Initialize subsystems
    initializeMessaging(this.config);
    initializeJobProcessing({ debug: this.config.debug });

    this.initialized = true;
    
    if (this.config.debug) {
      console.log('[Extension Service] Extension service initialized successfully');
    }
  }

  /**
   * Register handler for when jobs are received from extension
   */
  public onJobsReceived(handler: JobsReceivedHandler): () => void {
    if (!this.initialized) {
      console.warn('[Extension Service] Service not initialized. Call initialize() first.');
      return () => {};
    }

    return onJobsReceived(handler);
  }

  /**
   * Register handler for chunk transfer progress
   */
  public onChunkProgress(handler: ChunkProgressHandler): () => void {
    if (!this.initialized) {
      console.warn('[Extension Service] Service not initialized. Call initialize() first.');
      return () => {};
    }

    return onChunkProgress(handler);
  }

  /**
   * Register handler for extension errors
   */
  public onJobsError(handler: JobsErrorHandler): () => void {
    if (!this.initialized) {
      console.warn('[Extension Service] Service not initialized. Call initialize() first.');
      return () => {};
    }

    return onJobsError(handler);
  }

  /**
   * Check if we have received jobs from extension
   */
  public static hasReceivedJobs(): boolean {
    return hasReceivedExtensionJobs();
  }

  /**
   * Clear the received jobs flag
   */
  public static clearJobsFlag(): void {
    clearExtensionJobsFlag();
  }

  /**
   * Check if extension is likely installed (basic detection)
   */
  public static isExtensionLikelyInstalled(): boolean {
    // Simple check - if we've ever received jobs, extension is probably installed
    return ExtensionService.hasReceivedJobs();
  }

  /**
   * Get service status
   */
  public getStatus(): {
    initialized: boolean;
    hasReceivedJobs: boolean;
    config: ExtensionServiceConfig;
  } {
    return {
      initialized: this.initialized,
      hasReceivedJobs: ExtensionService.hasReceivedJobs(),
      config: { ...this.config }
    };
  }

  /**
   * Cleanup service
   */
  public cleanup(): void {
    if (this.config.debug) {
      console.log('[Extension Service] Cleaning up extension service');
    }

    cleanupMessaging();
    this.initialized = false;
  }

  /**
   * Log debug message (only in debug mode)
   */
  private log(...args: any[]): void {
    if (this.config.debug) {
      console.log('[Extension Service]', ...args);
    }
  }
}

// Export singleton instance
export const extensionService = ExtensionService.getInstance();

/**
 * Helper hook for React components to easily use extension service
 */
export function useExtensionService(config?: ExtensionServiceConfig) {
  // Initialize service on first use
  if (!extensionService.getStatus().initialized) {
    extensionService.initialize(config);
  }

  // Return a stable reference to prevent useEffect re-runs from object identity changes
  const serviceRef = useRef({
    // Core methods
    onJobsReceived: extensionService.onJobsReceived.bind(extensionService),
    onJobsError: extensionService.onJobsError.bind(extensionService),
    onChunkProgress: extensionService.onChunkProgress.bind(extensionService),

    // Status methods
    hasReceivedJobs: ExtensionService.hasReceivedJobs,
    clearJobsFlag: ExtensionService.clearJobsFlag,
    isExtensionLikelyInstalled: ExtensionService.isExtensionLikelyInstalled,
    getStatus: extensionService.getStatus.bind(extensionService),

    // Utility methods
    convertExtensionJobsToJobMarketDto,
    validateExtensionJobData,
    getJobStatistics,
    filterExtensionJobs
  });

  return serviceRef.current;
}